/**
 * MicrobiomeIntelligenceCard — Phase B Section 5.
 *
 * Three-axis educational module: protective / cariogenic / periodontal dysbiosis
 * microbiome factors.
 */
import React from "react";
import { Microscope, Sprout, AlertTriangle, Activity } from "lucide-react";
import ScoreExplanation from "@/components/dental_intel/ScoreExplanation";

const BAND_PILL = {
  green:  "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300",
  yellow: "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300",
  orange: "bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-300",
  red:    "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300",
};

export default function MicrobiomeIntelligenceCard({ data, isDark, lang = "bg", cardCls }) {
  const t = {
    bg: {
      title: "Орален микробиом — образователен баланс",
      balance: "Баланс на микробиома",
      protective: "Защитен микробиом",
      cariogenic: "Кариогенен микробиом",
      periodontal: "Пародонтална дисбиоза",
      protective_desc: "Стимулиран от фибри, полифеноли, omega-3",
      cariogenic_desc: "Стимулиран от чести захари, рафинирани въглехидрати",
      periodontal_desc: "Свързан с хронично възпаление, пушене, метаболитна дисфункция",
    },
    en: {
      title: "Oral microbiome — educational balance",
      balance: "Microbiome balance",
      protective: "Protective microbiome",
      cariogenic: "Cariogenic microbiome",
      periodontal: "Periodontal dysbiosis",
      protective_desc: "Driven by fiber, polyphenols, omega-3",
      cariogenic_desc: "Driven by frequent sugars, refined carbohydrates",
      periodontal_desc: "Linked to chronic inflammation, smoking, metabolic dysfunction",
    },
    es: {
      title: "Microbioma oral — equilibrio educativo",
      balance: "Equilibrio del microbioma",
      protective: "Microbioma protector",
      cariogenic: "Microbioma cariogénico",
      periodontal: "Disbiosis periodontal",
      protective_desc: "Impulsado por fibra, polifenoles, omega-3",
      cariogenic_desc: "Impulsado por azúcares frecuentes, carbohidratos refinados",
      periodontal_desc: "Relacionado con inflamación crónica, tabaquismo, disfunción metabólica",
    },
  }[lang] || {};

  return (
    <div className={`${cardCls} p-5`} data-testid="dhi-microbiome-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold flex items-center gap-2">
          <Microscope className="w-5 h-5 text-violet-500" />
          {t.title}
        </h3>
        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${BAND_PILL[data?.band || "yellow"]}`} data-testid="dhi-microbiome-score">
          {t.balance}: {data?.score ?? "—"}/100
        </span>
      </div>

      <div className="grid md:grid-cols-3 gap-3">
        <AxisBlock
          icon={Sprout}
          title={t.protective}
          desc={t.protective_desc}
          items={data?.protective}
          tone="emerald"
          isDark={isDark}
          testid="dhi-microbiome-protective"
        />
        <AxisBlock
          icon={AlertTriangle}
          title={t.cariogenic}
          desc={t.cariogenic_desc}
          items={data?.cariogenic}
          tone="amber"
          isDark={isDark}
          testid="dhi-microbiome-cariogenic"
        />
        <AxisBlock
          icon={Activity}
          title={t.periodontal}
          desc={t.periodontal_desc}
          items={data?.periodontal_dysbiosis}
          tone="rose"
          isDark={isDark}
          testid="dhi-microbiome-periodontal"
        />
      </div>

      <ScoreExplanation
        isDark={isDark}
        lang={lang}
        testid="dhi-microbiome-why"
        inputs={lang === "en"
          ? "Daily fiber, sugar exposure, DIL, meal frequency, smoking, alcohol, hs-CRP, HbA1c, omega-3 index."
          : "Дневни фибри, захарна експозиция, DIL, честота на хранене, тютюнопушене, алкохол, hs-CRP, HbA1c, омега-3 индекс."}
        formula={lang === "en"
          ? "Starts at 78. Adds ~6 per protective factor (fiber ≥ 25g, polyphenol-rich diet, omega-3 ≥ 8%). Subtracts ~7 per cariogenic factor (sugar > 25g, DIL > 90, snacking > 5/day) and per periodontal dysbiosis driver (smoking, hs-CRP > 3, HbA1c ≥ 5.7%)."
          : "Започва от 78. Добавя ~6 на защитен фактор (фибри ≥ 25g, полифенолна диета, омега-3 ≥ 8%). Изважда ~7 на кариогенен фактор (захар > 25g, DIL > 90, > 5 хранения/ден) и на пародонтална дисбиоза (пушене, hs-CRP > 3, HbA1c ≥ 5.7%)."}
        factors={[
          ...((data?.protective) || []).slice(0,2).map((f) => `✓ ${f}`),
          ...((data?.cariogenic) || []).slice(0,2).map((f) => `✗ ${f}`),
          ...((data?.periodontal_dysbiosis) || []).slice(0,2).map((f) => `✗ ${f}`),
        ]}
      />
    </div>
  );
}

function AxisBlock({ icon: Icon, title, desc, items, tone, isDark, testid }) {
  const bgCls = {
    emerald: isDark ? "bg-emerald-500/10 border-emerald-500/20" : "bg-emerald-50 border-emerald-200",
    amber:   isDark ? "bg-amber-500/10 border-amber-500/20"     : "bg-amber-50 border-amber-200",
    rose:    isDark ? "bg-rose-500/10 border-rose-500/20"       : "bg-rose-50 border-rose-200",
  }[tone];
  const iconCls = {
    emerald: "text-emerald-500",
    amber:   "text-amber-500",
    rose:    "text-rose-500",
  }[tone];

  return (
    <div className={`p-3 rounded-lg border ${bgCls}`} data-testid={testid}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className={`w-4 h-4 ${iconCls}`} />
        <span className="text-xs font-bold">{title}</span>
      </div>
      <div className="text-[10px] opacity-60 mb-2 leading-snug">{desc}</div>
      <ul className="space-y-1 text-xs">
        {items && items.length ? items.map((f, i) => (
          <li key={i} className="flex items-start gap-1.5">
            <span className={`mt-1 w-1 h-1 rounded-full shrink-0 ${iconCls.replace("text-", "bg-")}`} />
            <span className="opacity-80 leading-snug">{f}</span>
          </li>
        )) : (
          <li className="opacity-40 italic text-[10px]">—</li>
        )}
      </ul>
    </div>
  );
}
